import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, AlertCircle, ArrowLeft } from 'lucide-react';
import { useAssessment } from '../hooks/useAssessment';
import { quizData } from '../data/questions';
import LoadingSpinner from '../components/LoadingSpinner';

const ResultsPage = () => {
  const navigate = useNavigate();
  const { assessment, loading } = useAssessment();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!assessment || !assessment.scores) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">No assessment results found</p>
          <button 
            onClick={() => navigate('/couple/dashboard')}
            className="text-purple-600 hover:underline"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const { scores } = assessment;
  const factorScores = [
    scores.factor1,
    scores.factor2,
    scores.factor3,
    scores.factor4,
    scores.factor5
  ];

  const getInterpretation = (score, isTotal = false) => {
    if (isTotal) {
      if (score >= 176) return { text: 'Strong compatibility and readiness for marriage', color: 'green' };
      if (score >= 101) return { text: 'Moderate compatibility (counseling beneficial)', color: 'yellow' };
      return { text: 'Significant compatibility concerns (counseling recommended)', color: 'red' };
    } else {
      if (score >= 36) return { text: 'High compatibility', color: 'green' };
      if (score >= 21) return { text: 'Moderate compatibility', color: 'yellow' };
      return { text: 'Low compatibility', color: 'red' };
    }
  };

  const totalInterpretation = getInterpretation(scores.total, true);

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Heart className="w-6 h-6 text-purple-600" />
            <span className="font-semibold text-lg">Assessment Results</span>
          </div>
          <button 
            onClick={() => navigate('/couple/dashboard')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </button>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-white p-8 rounded-xl shadow-sm border mb-6">
          <h1 className="text-3xl font-bold mb-6">Your Assessment Results</h1>
          
          {/* Overall Score */}
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-6 rounded-lg mb-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <p className="text-gray-600 mb-2">Overall Score</p>
                <p className="text-5xl font-bold text-purple-600">{scores.total}/250</p>
              </div>
              <div className={`px-6 py-3 rounded-lg text-center ${
                totalInterpretation.color === 'green' ? 'bg-green-100 text-green-700' :
                totalInterpretation.color === 'yellow' ? 'bg-yellow-100 text-yellow-700' :
                'bg-red-100 text-red-700'
              }`}>
                <p className="font-semibold">{totalInterpretation.text}</p>
              </div>
            </div>
          </div>

          {/* Factor Breakdown */}
          <div className="space-y-4">
            <h2 className="text-xl font-semibold mb-4">Factor Breakdown</h2>
            {quizData.factors.map((factor, idx) => {
              const score = factorScores[idx];
              const interpretation = getInterpretation(score);
              
              return (
                <div key={idx} className="p-4 border rounded-lg">
                  <div className="flex justify-between items-start mb-3 flex-wrap gap-2">
                    <div>
                      <h3 className="font-semibold">{factor.name}</h3>
                      <p className="text-sm text-gray-600">{interpretation.text}</p>
                    </div>
                    <span className="text-2xl font-bold text-purple-600">{score}/50</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div 
                      className={`h-3 rounded-full transition-all ${
                        interpretation.color === 'green' ? 'bg-green-500' :
                        interpretation.color === 'yellow' ? 'bg-yellow-500' :
                        'bg-red-500'
                      }`}
                      style={{width: `${(score / 50) * 100}%`}}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Score Legend */}
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="font-semibold mb-3 text-sm">Score Interpretation Guide</h3>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-green-500 rounded"></div>
                <span>36-50 points: High compatibility</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-yellow-500 rounded"></div>
                <span>21-35 points: Moderate compatibility</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-red-500 rounded"></div>
                <span>10-20 points: Low compatibility</span>
              </div>
            </div>
          </div>

          {/* Next Steps */}
          <div className="mt-8 p-6 bg-blue-50 border border-blue-200 rounded-lg">
            <h3 className="font-semibold mb-2 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-blue-600" />
              Next Steps
            </h3>
            <p className="text-sm text-gray-700 mb-3">
              Your counselor will review these results and schedule a follow-up session. 
              Results will be shared with your partner only after counselor approval.
            </p>
            <p className="text-sm text-gray-700">
              Remember, these results are a tool for growth and conversation. 
              Every couple has strengths and areas for development. What matters most is 
              your commitment to work together.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResultsPage;